import {
    IAssetFile,
    IAssetFileDescriptor,
    IAssetFileLayoutDescriptor,
    IAssetFileSkin,
    IAssetFileSkinOverride
} from "./interfaces";
import {NullableString, NullableUndefinedString} from "../../../common/types/nullable-types";
import {Lazy} from "../../../utils/lazy";

export class AssetFile implements IAssetFile {
    constructor(private readonly descriptor: IAssetFileDescriptor) {
    }

    private get skin(): string {
        return this.descriptor.context.skin;
    }

    private get skinOverride(): string {
        return this.descriptor.context.skinOverride;
    }

    private get language(): string {
        return this.descriptor.context.language;
    }

    private get miniOrbit(): NullableUndefinedString {
        return this.descriptor.context.miniOrbit;
    }


    private layoutRoot(layoutRelativePath: string): string {
        let origin = this.descriptor.context.serverOrigin;
        if(!origin.endsWith('/')) {
            origin += '/';
        }
        return `${origin}cgp-assets/${layoutRelativePath}`;
    }

    private _resolvedPath = new Lazy<string>(() => this._resolvePath());

    toString(): string {
        return this._resolvedPath.value;
    }


    private _resolvePath(): string {
        let path: NullableString = null;
        switch (this.miniOrbit?.toLowerCase()) {
            case 'cip':
                path = this._resolvePathForLayout("mini/cip", this.descriptor.mini?.cip);
                break;
            case 'cis':
                path = this._resolvePathForLayout("mini/cis", this.descriptor.mini?.cis);
                break;
        }

        if(path) {
            return path;
        }

        return this._resolvePathForLayout('full', this.descriptor.full) ?? "";
    }

    private _resolvePathForLayout(layoutRelativePath: string, layout: IAssetFileLayoutDescriptor | undefined): NullableString {

        if(!layout) {
            return null;
        }

        let path = this._resolveSkin(layout);
        if(!path) {
            path = this._resolveLanguage(layout);
        }

        const layoutRoot = this.layoutRoot(layoutRelativePath);
        if(path) {
            return `${layoutRoot}/${path}`;
        }

        if(layout.defaults) {
            return `${layoutRoot}/defaults/${this.descriptor.rootFolder}/${this.descriptor.path}`;
        }
        return null;
    }

    private _resolveLanguage(layout: IAssetFileLayoutDescriptor): NullableString {
        if(layout.languages && layout.languages[this.language]) {
            return `lang/${this.language}/${this.descriptor.rootFolder}/${this.descriptor.path}`;
        }
        return null;
    }

    private _resolveSkin(layout: IAssetFileLayoutDescriptor): NullableString {
        const fileSkin = layout.skins && layout.skins[this.skin];
        if(!fileSkin) {
            return null;
        }
        let path = this._resolveSkinOverride(fileSkin);
        if(!path) {
            path = this._resolveSkinLanguage(fileSkin);
        }

        if(!path && fileSkin.defaults) {
            path = `defaults/${this.descriptor.rootFolder}/${this.descriptor.path}`;
        }

        if(path) {
            return `skins/${this.skin}/${path}`;
        }

        return null;

    }

    private _resolveSkinLanguage(fileSkin: IAssetFileSkin): NullableString {
        if(fileSkin.languages && fileSkin.languages[this.language]) {
            return `lang/${this.language}/${this.descriptor.rootFolder}/${this.descriptor.path}`
        }

        return null;
    }

    private _resolveSkinOverride(fileSkin: IAssetFileSkin): NullableString {
        let fileSkinOverride = fileSkin.skinOverrides && fileSkin.skinOverrides[this.skinOverride];
        if(!fileSkinOverride) {
            return null;
        }

        let path = this._resolveSkinOverrideLanguage(fileSkinOverride);

        if(!path && fileSkinOverride.defaults) {
            path = `defaults/${this.descriptor.rootFolder}/${this.descriptor.path}`;
        }

        if(path) {
            return `overrides/${this.skinOverride}/${path}`;
        }

        return null;

    }

    private _resolveSkinOverrideLanguage(fileSkinOverride: IAssetFileSkinOverride): NullableString {
        if(fileSkinOverride.languages && fileSkinOverride.languages[this.language]) {
            return `lang/${this.language}/${this.descriptor.rootFolder}/${this.descriptor.path}`
        }

        return null;
    }
}